import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../store/actions";
import { isEmpty } from 'lodash';
import {
  Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography,
} from '@mui/material';

import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import Spinner from '../../../components/Spinner/index';
import { i18n } from "../../../translate/i18n";
import { formatLanguageFromLocalStorage } from '../../../utils/formatLanguageFromLocalStorage';

export default function ParamEquipments(props) {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
	const [firstLoad, setFirstLoad] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  

  const localStorageLang = localStorage.getItem("i18nextLng");
  const emailLanguage = formatLanguageFromLocalStorage(localStorageLang);

  const fetchEquipmentModels = useCallback(
    () => dispatch(actions.fetchEquipmentModels(page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
	);
	
	const fetchEquipmentModelsExport = useCallback(
    () => dispatch(actions.fetchEquipmentModelsExport(searchText, order, orderBy, emailLanguage)),
    [searchText, order, orderBy, emailLanguage]
	);
	
  const equipmentModels = useSelector(state => state.equipmentModels.equipmentModels);
  const equipmentModelsFilter = useSelector(state => state.equipmentModels.equipmentModelsFilter);
  const loading = useSelector(state => state.equipmentModels.loading);

  const headRows = [
    { id: "name", numeric: false, disablePadding: false, label: i18n.t('headRowsEquip.name')},
    { id: "equipmentGroup.name", numeric: false, disablePadding: false, label: i18n.t('headRowsEquip.equipmentGroup') },
    { id: "checklist", numeric: false, disablePadding: false, label: i18n.t('headRowsEquip.checklist') },
  ];

  useEffect(() => {
    if (!isEmpty(equipmentModelsFilter)) {
      setPage(equipmentModelsFilter.page);
      setLimit(equipmentModelsFilter.limit);
      setSearchText(equipmentModelsFilter.searchText);
      setOrder(equipmentModelsFilter.order);
      setOrderBy(equipmentModelsFilter.orderBy);
    }
    setFirstLoad(false);
  }, []);

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

  useEffect(() => {
    if (!firstLoad) {
      fetchEquipmentModels();
      setAlreadySerched(true);
    }
  }, [firstLoad, page, limit, order, orderBy]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchEquipmentModels();
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const addNewAreaRow = () => {
    props.history.push('form-equipments-param/new/admin-equipments')
  }

  const selectRow = (e, id) => {
    props.history.push(`form-equipments-param/${id}/admin-equipments`)
	}
	
	const exportExcel = () => {
    setExportLoading(true);

    fetchEquipmentModelsExport()
      .then(response => {
        if (response) {
          window.open(response.url, "_blank")
        }
        setExportLoading(false);
      })
      .catch(() => setExportLoading(false))
  }

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    tableBody: {
      cursor: 'pointer',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    chip: {
      margin: '5px'
    },
  }

  return <>
    <Container maxWidth='md'>
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleEquip.title')}
          placeholder={i18n.t('tableTitleEquip.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
                      addRow={() => addNewAreaRow()}
                      clickExport={exportExcel}
          downloading={exportLoading}
          exportButtonDisabled={loading}
        />
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {equipmentModels.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('fetchEquip.text')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody style={{ ...styles.tableBody, ...styles.opacityContainer }}>
            {equipmentModels.rows.map((equip, index) => (
              <TableRow key={equip._id}
                hover
                onClick={event => selectRow(event, equip._id)}
              >
                <TableCell>{equip.name}</TableCell>
                <TableCell>{equip.equipmentGroup.name}</TableCell>
                <TableCell>{
                  equip.equipmentServices && equip.equipmentServices.some(item =>
                    item.questions && item.questions.length > 0) ? i18n.t('tableCellEquip.cellHaveChecklist') : i18n.t('tableCellEquip.cellNotHaveChecklist')
                }</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={equipmentModels.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;
}
